import React from 'react';
import {
    Truck, Zap, Headphones, Sparkles, Coffee, Heart,
    MapPin, Phone, MessageCircle, Clock, ArrowRight, ChevronDown
} from 'lucide-react';
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../../ui/accordion';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useTenantTheme } from '../TenantThemeContext';
import {
    GlassCard, RevealOnScroll, SectionHeader, AmbientBlob, MagneticButton, GridBackdrop,
    TemplateCard, TemplateDivider
} from '../ui/primitives';
import SectionParticles from '../ui/SectionParticles';

// ============== CONVENIENCE PROMISES ==============
const CONVENIENCE_DEFS = {
    pickup_drop: { icon: Truck, en: 'Pickup & Drop', bn: 'পিকআপ ও ড্রপ', body_en: 'Free pickup within city', body_bn: 'শহরের মধ্যে ফ্রি পিকআপ' },
    express_lane: { icon: Zap, en: 'Express Lane', bn: 'এক্সপ্রেস লেন', body_en: 'Service in 60 minutes', body_bn: '৬০ মিনিটে সার্ভিস' },
    emergency_24x7: { icon: Headphones, en: '24/7 Emergency', bn: '২৪/৭ ইমার্জেন্সি', body_en: 'On-call roadside help', body_bn: 'রাস্তায় তাৎক্ষণিক সাহায্য' },
    free_wash: { icon: Sparkles, en: 'Free Wash', bn: 'ফ্রি ওয়াশ', body_en: 'Every full service', body_bn: 'প্রতি ফুল সার্ভিসে' },
    lounge: { icon: Coffee, en: 'Customer Lounge', bn: 'কাস্টমার লাউঞ্জ', body_en: 'Wait in comfort with WiFi', body_bn: 'WiFi সহ আরামে অপেক্ষা' },
    female_friendly: { icon: Heart, en: 'Female-Friendly', bn: 'নারীবান্ধব', body_en: 'Safe, respectful service', body_bn: 'নিরাপদ ও সম্মানজনক' }
};

export function Convenience() {
    const { tenant, sectionEnabled, template } = useTenantTheme();
    const { language } = useLanguage();
    if (!sectionEnabled('convenience')) return null;

    const promises = tenant?.convenience_promises || {};
    const enabled = Object.entries(CONVENIENCE_DEFS).filter(([k]) => promises[k]?.enabled);
    if (enabled.length === 0) return null;

    const phone = tenant?.phone;
    const whatsapp = tenant?.whatsapp_number || phone;
    const tk = template?.tokens || {};

    return (
        <section className={['relative', tk.sectionPadding || 'py-16 md:py-20'].join(' ')} id="convenience">
            <AmbientBlob className="-bottom-32 -right-20" size={500} delay={0.4} />
            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <SectionHeader
                    eyebrow={language === 'bn' ? 'সুবিধাসমূহ' : 'On-the-house extras'}
                    title="More than a workshop"
                    titleBn="শুধু ওয়ার্কশপ নয়, আরও কিছু"
                    stamp="06"
                />
                <div className={['mt-12', tk.featureGrid || 'grid sm:grid-cols-2 md:grid-cols-3 gap-5'].join(' ')}>
                    {enabled.map(([key, def], i) => {
                        const Icon = def.icon;
                        const custom = promises[key] || {};
                        const title = language === 'bn' ? (custom.title_bn || def.bn) : (custom.title || def.en);
                        const body = language === 'bn' ? (custom.body_bn || def.body_bn) : (custom.body || def.body_en);
                        return (
                            <RevealOnScroll key={key} delay={i * 0.05}>
                                <TemplateCard
                                    index={i}
                                    icon={Icon}
                                    title={title}
                                    body={body}
                                    languageBn={language === 'bn'}
                                />
                            </RevealOnScroll>
                        );
                    })}
                </div>

                {(phone || whatsapp) && (
                    <RevealOnScroll className="mt-12 text-center">
                        <div className="inline-flex flex-wrap items-center justify-center gap-3">
                            {whatsapp && (
                                <MagneticButton as="a" href={`https://wa.me/${String(whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" variant="primary" icon={<MessageCircle className="w-4 h-4" />}>
                                    WhatsApp
                                </MagneticButton>
                            )}
                            {phone && (
                                <MagneticButton as="a" href={`tel:${phone}`} variant="secondary" icon={<Phone className="w-4 h-4" />}>
                                    {language === 'bn' ? 'কল করুন' : 'Call now'}
                                </MagneticButton>
                            )}
                        </div>
                    </RevealOnScroll>
                )}
            </div>
        </section>
    );
}

// ============== MAP & HOURS ==============
const DAY_LABELS = {
    en: { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' },
    bn: { mon: 'সোমবার', tue: 'মঙ্গলবার', wed: 'বুধবার', thu: 'বৃহস্পতিবার', fri: 'শুক্রবার', sat: 'শনিবার', sun: 'রবিবার' }
};
const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Convert "HH:MM" 24h -> "h:MM AM/PM"
function to12h(t) {
    if (!t || typeof t !== 'string') return t || '—';
    const m = /^(\d{1,2}):(\d{2})/.exec(t);
    if (!m) return t;
    let h = parseInt(m[1], 10);
    const mm = m[2];
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${mm} ${ampm}`;
}

// Returns true when current time falls inside the given day's open–close window.
function isOpenNow(h) {
    if (!h || h.closed || !h.open || !h.close) return false;
    const now = new Date();
    const cur = now.getHours() * 60 + now.getMinutes();
    const [oh, om] = h.open.split(':').map(Number);
    const [ch, cm] = h.close.split(':').map(Number);
    if (Number.isNaN(oh) || Number.isNaN(ch)) return false;
    const open = oh * 60 + (om || 0);
    let close = ch * 60 + (cm || 0);
    // Handle past-midnight close (e.g. 22:00 – 02:00)
    if (close <= open) close += 24 * 60;
    const curWrapped = cur < open ? cur + 24 * 60 : cur;
    return curWrapped >= open && curWrapped < close;
}

export function MapSection() {
    const { tenant, sectionEnabled } = useTenantTheme();
    const { language } = useLanguage();
    // Backend stores opening_hours as List[{day,open,close,closed}], but UI keys by day.
    // Normalize either shape so today's status & weekly schedule display correctly.
    const rawHours = tenant?.opening_hours;
    const hours = React.useMemo(() => {
        if (!rawHours) return {};
        if (Array.isArray(rawHours)) {
            const out = {};
            rawHours.forEach(item => {
                if (item && typeof item === 'object' && item.day) {
                    const { day, ...rest } = item;
                    out[day] = rest;
                }
            });
            return out;
        }
        return rawHours;
    }, [rawHours]);
    if (!sectionEnabled('map')) return null;

    const lat = tenant?.latitude;
    const lng = tenant?.longitude;
    const address = tenant?.address || tenant?.location;
    const labels = DAY_LABELS[language === 'bn' ? 'bn' : 'en'];

    const today = DAY_ORDER[(new Date().getDay() + 6) % 7]; // Mon=0
    const openNow = isOpenNow(hours[today]);

    const embed = lat && lng
        ? `https://www.google.com/maps?q=${lat},${lng}&z=16&output=embed`
        : address
            ? `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`
            : null;

    const directionsUrl = lat && lng
        ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
        : address
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
            : '#';

    return (
        <section className="relative py-16 md:py-20 bg-[var(--tenant-surface-alt)]/30" id="map">
            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <SectionHeader
                    eyebrow={language === 'bn' ? 'অবস্থান' : 'Find us'}
                    title="Stop by the workshop"
                    titleBn="ওয়ার্কশপে ঢুঁ মারুন"
                />
                <div className="mt-12 grid lg:grid-cols-5 gap-6">
                    <RevealOnScroll className="lg:col-span-3">
                        <GlassCard className="overflow-hidden relative">
                            {embed ? (
                                <iframe
                                    title="Workshop location"
                                    src={embed}
                                    className="w-full aspect-[4/3] md:aspect-[16/10] grayscale-[35%] contrast-[1.05]"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            ) : (
                                <div className="aspect-[16/10] flex items-center justify-center text-[var(--tenant-muted)]">
                                    {language === 'bn' ? 'ম্যাপ পাওয়া যায়নি' : 'Map not configured'}
                                </div>
                            )}
                            {/* Premium theme-tint overlay — gives the embed a curated, on-brand
                                feel instead of the raw Google Maps look. Pointer-events none
                                so the iframe stays interactive. */}
                            {embed && (
                                <>
                                    <div
                                        className="pointer-events-none absolute inset-0"
                                        style={{
                                            background: 'linear-gradient(135deg, color-mix(in srgb, var(--tenant-primary) 22%, transparent) 0%, transparent 45%, color-mix(in srgb, var(--tenant-surface) 35%, transparent) 100%)',
                                            mixBlendMode: 'multiply'
                                        }}
                                    />
                                    <div
                                        className="pointer-events-none absolute inset-0"
                                        style={{
                                            background: 'radial-gradient(120% 80% at 50% 100%, color-mix(in srgb, var(--tenant-surface) 60%, transparent) 0%, transparent 55%)'
                                        }}
                                    />
                                    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[var(--tenant-primary)]/20 rounded-[inherit]" />
                                    {/* Address pin — scoped, branded label */}
                                    <div className="pointer-events-none absolute left-5 bottom-5 right-5 md:left-6 md:bottom-6 md:right-auto md:max-w-sm">
                                        <div className="inline-flex items-start gap-2.5 px-4 py-2.5 rounded-2xl bg-[var(--tenant-surface)]/95 backdrop-blur-md border border-[var(--tenant-primary)]/30 shadow-[0_18px_40px_-22px_var(--tenant-primary)]">
                                            <MapPin className="w-4 h-4 mt-0.5 text-[var(--tenant-primary)] shrink-0" />
                                            <div className="text-xs leading-snug text-[var(--tenant-ink)]">
                                                <span className="font-display text-sm tracking-wide">{tenant?.name}</span>
                                                {address && <div className="text-[var(--tenant-muted)] mt-0.5 line-clamp-2">{address}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </GlassCard>
                    </RevealOnScroll>
                    <RevealOnScroll className="lg:col-span-2" delay={0.1}>
                        <GlassCard className="p-6 h-full">
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-[var(--tenant-primary)] mt-0.5" />
                                <address className="not-italic text-[var(--tenant-ink)] leading-relaxed flex-1">
                                    <div className="flex items-center gap-2 justify-between flex-wrap">
                                        <span className="font-display text-2xl tracking-wide">{tenant?.name}</span>
                                        {/* Open-now / closed pill — visitor's local time, themed via primary color */}
                                        <span
                                            className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold"
                                            style={openNow ? {
                                                color: 'var(--tenant-primary)',
                                                background: 'color-mix(in srgb, var(--tenant-primary) 14%, transparent)',
                                                border: '1px solid color-mix(in srgb, var(--tenant-primary) 35%, transparent)'
                                            } : {
                                                color: 'var(--tenant-muted)',
                                                background: 'color-mix(in srgb, var(--tenant-muted) 12%, transparent)',
                                                border: '1px solid var(--tenant-border)'
                                            }}
                                        >
                                            <span className="relative flex w-2 h-2">
                                                {openNow && (
                                                    <span
                                                        className="absolute inset-0 rounded-full opacity-75 animate-ping"
                                                        style={{ background: 'var(--tenant-primary)' }}
                                                    />
                                                )}
                                                <span
                                                    className="relative inline-flex w-2 h-2 rounded-full"
                                                    style={{ background: openNow ? 'var(--tenant-primary)' : 'color-mix(in srgb, var(--tenant-muted) 60%, transparent)' }}
                                                />
                                            </span>
                                            {openNow
                                                ? (language === 'bn' ? 'এখন খোলা' : 'Open now')
                                                : (language === 'bn' ? 'এখন বন্ধ' : 'Closed')}
                                        </span>
                                    </div>
                                    <div className="text-sm text-[var(--tenant-muted)] mt-1">{address}</div>
                                </address>
                            </div>

                            <div className="mt-6">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[var(--tenant-muted)] mb-3">
                                    <Clock className="w-3.5 h-3.5" /> {language === 'bn' ? 'খোলার সময়' : 'Opening hours'}
                                </div>
                                <ul className="space-y-1.5 text-sm">
                                    {DAY_ORDER.map(d => {
                                        const h = hours[d];
                                        const isToday = d === today;
                                        const text = !h || h.closed
                                            ? (language === 'bn' ? 'বন্ধ' : 'Closed')
                                            : `${to12h(h.open)} – ${to12h(h.close)}`;
                                        return (
                                            <li key={d} className={['flex items-center justify-between', isToday ? 'text-[var(--tenant-primary)] font-semibold' : 'text-[var(--tenant-ink)]'].join(' ')}>
                                                <span>{labels[d]}{isToday && <span className="ml-2 text-[10px] uppercase tracking-[0.2em]">• today</span>}</span>
                                                <time className="font-mono text-xs">{text}</time>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <div className="mt-6 flex flex-wrap gap-3">
                                <MagneticButton as="a" href={directionsUrl} target="_blank" rel="noopener noreferrer" size="sm" icon={<ArrowRight className="w-4 h-4" />}>
                                    {language === 'bn' ? 'দিকনির্দেশনা' : 'Get directions'}
                                </MagneticButton>
                                {tenant?.phone && (
                                    <MagneticButton as="a" href={`tel:${tenant.phone}`} variant="ghost" size="sm" icon={<Phone className="w-4 h-4" />}>
                                        {tenant.phone}
                                    </MagneticButton>
                                )}
                            </div>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </div>
        </section>
    );
}

// ============== FAQ ==============
const FAQ_FALLBACK = [
    { q: 'Do you offer warranty on repairs?', q_bn: 'মেরামতে কি ওয়ারেন্টি দেওয়া হয়?', a: 'Yes — every repair carries a workshop warranty. Ask the desk for terms.', a_bn: 'হ্যাঁ — প্রতিটি মেরামতে ওয়ার্কশপ ওয়ারেন্টি দেওয়া হয়। ডেস্কে শর্তাবলী জানতে পারবেন।' },
    { q: 'How long does a service take?', q_bn: 'সার্ভিসে কতক্ষণ সময় লাগে?', a: 'Standard service is usually 60–90 minutes. Heavy repairs are quoted before work begins.', a_bn: 'সাধারণ সার্ভিস ৬০–৯০ মিনিট। বড় কাজের জন্য আগে কোটেশন দেওয়া হয়।' },
    { q: 'Do you use genuine parts?', q_bn: 'কি জেনুইন পার্টস ব্যবহার করেন?', a: 'We default to genuine OEM parts. Aftermarket options are offered transparently when requested.', a_bn: 'আমরা সাধারণত জেনুইন OEM পার্টস ব্যবহার করি। অনুরোধে অন্য বিকল্পও স্বচ্ছভাবে দেখানো হয়।' },
    { q: 'Can I track my bike\'s status?', q_bn: 'আমি কি বাইকের স্ট্যাটাস ট্র্যাক করতে পারি?', a: 'Yes — use the live tracker above with your phone and job number.', a_bn: 'হ্যাঁ — উপরের লাইভ ট্র্যাকারে ফোন ও জব নম্বর দিয়ে দেখুন।' }
];

export function FAQ() {
    const { tenant, sectionEnabled } = useTenantTheme();
    const { language } = useLanguage();
    if (!sectionEnabled('faq')) return null;

    const items = (tenant?.faq && tenant.faq.length > 0) ? tenant.faq : FAQ_FALLBACK;

    return (
        <section className="relative py-16 md:py-20" id="faq">
            <div className="relative max-w-3xl mx-auto px-5 md:px-8">
                <SectionHeader
                    align="center"
                    eyebrow={language === 'bn' ? 'প্রশ্নোত্তর' : 'FAQ'}
                    title="Questions, answered"
                    titleBn="আপনার প্রশ্নের উত্তর"
                />
                <div className="mt-10">
                    <Accordion type="single" collapsible className="space-y-4">
                        {items.map((it, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="group border border-[var(--tenant-border)] rounded-2xl bg-[var(--tenant-surface)] px-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-26px_var(--tenant-primary)] hover:border-[var(--tenant-primary)]/50 data-[state=open]:border-[var(--tenant-primary)]/60 data-[state=open]:shadow-[0_18px_40px_-26px_var(--tenant-primary)]"
                            >
                                <AccordionTrigger className={['text-left py-5 hover:no-underline text-[var(--tenant-ink)] font-semibold text-base md:text-[17px] leading-snug normal-case tracking-normal group-hover:text-[var(--tenant-primary)] transition-colors', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                                    {(language === 'bn' && it.q_bn) ? it.q_bn : it.q}
                                </AccordionTrigger>
                                <AccordionContent className={['text-[var(--tenant-muted)] leading-relaxed pb-6 text-[15px] md:text-base', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                                    {(language === 'bn' && it.a_bn) ? it.a_bn : it.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
}

// ============== FINAL CTA ==============
export function FinalCTA() {
    const { tenant, sectionEnabled } = useTenantTheme();
    const { language } = useLanguage();
    if (!sectionEnabled('final_cta')) return null;

    const phone = tenant?.phone;
    const whatsapp = tenant?.whatsapp_number || phone;

    return (
        <section className="relative py-20 md:py-24 overflow-hidden" id="get-started">
            <SectionParticles density={36} repel={0.08} />
            <AmbientBlob className="top-0 left-1/4" size={600} />
            <AmbientBlob className="bottom-0 right-1/4" size={500} delay={0.6} />
            <GridBackdrop opacity={0.05} />
            <div className="relative max-w-5xl mx-auto px-5 md:px-8 text-center">
                <RevealOnScroll>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--tenant-border)] bg-[var(--tenant-surface)]/60 backdrop-blur text-xs uppercase tracking-[0.28em] text-[var(--tenant-muted)]">
                        {language === 'bn' ? 'গাড়ি/বাইক নিয়ে আসুন' : 'Bring it in. We\'ll handle the rest.'}
                    </div>
                    <h2 className={['mt-6 font-display text-6xl md:text-8xl tracking-wider text-[var(--tenant-ink)]', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                        {language === 'bn' ? 'আপনার গাড়ি পাবে বিশেষজ্ঞদের যত্ন।' : 'Your vehicle deserves expert care.'}
                    </h2>
                    <p className={['mt-5 text-lg text-[var(--tenant-muted)] max-w-2xl mx-auto', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                        {language === 'bn'
                            ? 'একটি কল বা WhatsApp মেসেজ — চাবি বুঝে নেওয়া, কাজ শুরু, এবং স্ট্যাটাস ডেলিভারি।'
                            : 'One call or WhatsApp message — keys handed over, work begins, status delivered.'}
                    </p>
                    <div className="mt-10 inline-flex flex-wrap items-center justify-center gap-3">
                        <MagneticButton as="a" href="#booking" size="lg" icon={<ArrowRight className="w-4 h-4" />}>
                            {language === 'bn' ? 'বুকিং করুন' : 'Book a slot'}
                        </MagneticButton>
                        {whatsapp && (
                            <MagneticButton as="a" href={`https://wa.me/${String(whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" variant="secondary" size="lg" icon={<MessageCircle className="w-4 h-4" />}>
                                WhatsApp
                            </MagneticButton>
                        )}
                        {phone && (
                            <MagneticButton as="a" href={`tel:${phone}`} variant="ghost" size="lg" icon={<Phone className="w-4 h-4" />}>
                                {phone}
                            </MagneticButton>
                        )}
                    </div>
                </RevealOnScroll>
            </div>
        </section>
    );
}
